/*
 * InspectBot vehicle configurator
 *
 * WheelPicker mirrors the scroll-snap picker from the original React build:
 * three visible rows, the centre row is the selection, and scrolling settles
 * onto the nearest row. Row height is read from CSS so the Theme Editor's
 * size setting stays the single source of truth.
 */

class WheelPicker {
  constructor(root, { onChange, onPick, trigger } = {}) {
    this.root = root;
    this.list = root.querySelector('[data-wheel-list]');
    this.field = root.dataset.field;
    this.onChange = onChange || function () {};
    // Fires only on an explicit tap of an option — never from scroll-settle.
    // The mobile picker modal uses this (not onChange) to decide when to
    // auto-close, so scrolling the wheel doesn't dismiss the modal.
    this.onPick = onPick || function () {};
    this.trigger = trigger || null;
    this.options = [];
    this.value = null;
    this.disabled = false;
    this.scrollTimer = null;
    this.lastEmitted = null;

    this.list.addEventListener('scroll', () => this.handleScroll());
    this.list.addEventListener('keydown', (event) => this.handleKeydown(event));
  }

  /**
   * Scroll offset that puts the given row under the centre indicator.
   * Measured from the live layout rather than derived from the row-height
   * setting, so it stays correct whatever the CSS actually resolves to.
   */
  offsetForButton(button) {
    return button.offsetTop - (this.list.clientHeight - button.offsetHeight) / 2;
  }

  buttonForValue(value) {
    if (value == null) return null;
    return this.list.querySelector(`.wheel-picker__option[data-value="${CSS.escape(value)}"]`);
  }

  /** Index of the row currently sitting closest to the centre indicator. */
  indexAtCentre() {
    const buttons = this.list.querySelectorAll('.wheel-picker__option');
    if (buttons.length === 0) return -1;

    const centre = this.list.scrollTop + this.list.clientHeight / 2;
    let bestIndex = 0;
    let bestDistance = Infinity;

    buttons.forEach((button, index) => {
      const buttonCentre = button.offsetTop + button.offsetHeight / 2;
      const distance = Math.abs(buttonCentre - centre);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });

    return bestIndex;
  }

  setOptions(options) {
    this.options = Array.isArray(options) ? options : [];
    this.lastEmitted = null;

    // Drop a selection that no longer exists in the new option set.
    if (this.value && !this.options.some((option) => option.value === this.value)) {
      this.value = null;
    }

    this.render();
    this.scrollToValue(false);
  }

  setValue(value, { emit = true } = {}) {
    if (this.value === value) return;
    this.value = value;
    this.lastEmitted = value;
    this.syncSelectedAttributes();
    this.scrollToValue(true);
    if (emit) this.onChange(this.field, value);
  }

  setDisabled(disabled) {
    this.disabled = Boolean(disabled);
    this.root.dataset.disabled = String(this.disabled);
    this.list.setAttribute('aria-disabled', String(this.disabled));
    this.list.tabIndex = this.disabled ? -1 : 0;
    if (this.trigger) this.trigger.disabled = this.disabled;
  }

  clear() {
    this.value = null;
    this.lastEmitted = null;
    this.syncSelectedAttributes();
    this.list.scrollTop = 0;
  }

  render() {
    const emptyText = this.list.dataset.emptyText || '';
    this.list.textContent = '';

    if (this.options.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'wheel-picker__empty';
      empty.textContent = emptyText;
      this.list.appendChild(empty);
      return;
    }

    this.list.appendChild(this.createPad());

    this.options.forEach((option, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'wheel-picker__option';
      button.dataset.index = String(index);
      button.dataset.value = option.value;
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', String(option.value === this.value));
      button.tabIndex = option.value === this.value ? 0 : -1;

      if (option.icon) {
        const icon = document.createElement('img');
        icon.className = 'wheel-picker__option-icon';
        icon.src = option.icon;
        icon.alt = '';
        icon.loading = 'lazy';
        button.appendChild(icon);
      }

      const text = document.createElement('span');
      text.className = 'wheel-picker__option-text';
      text.textContent = option.label;
      button.appendChild(text);

      // Focusing a button makes the browser scroll it just inside the viewport,
      // which fights our centring. Suppress the focus that a mouse press would
      // give it and centre the row ourselves instead.
      button.addEventListener('mousedown', (event) => event.preventDefault());

      button.addEventListener('click', () => {
        if (this.disabled) return;
        this.setValue(option.value);
        // A deliberate tap always counts as a "pick", even if this option was
        // already the selected one (setValue no-ops in that case).
        this.onPick(this.field, option.value);
      });

      this.list.appendChild(button);
    });

    this.list.appendChild(this.createPad());
  }

  createPad() {
    const pad = document.createElement('div');
    pad.className = 'wheel-picker__pad';
    pad.setAttribute('aria-hidden', 'true');
    return pad;
  }

  syncSelectedAttributes() {
    this.list.querySelectorAll('.wheel-picker__option').forEach((button) => {
      const selected = button.dataset.value === this.value;
      button.setAttribute('aria-selected', String(selected));
      button.tabIndex = selected ? 0 : -1;
    });
  }

  scrollToValue(smooth, attempt = 0) {
    if (this.options.length === 0) return;

    const button = this.buttonForValue(this.value);

    if (!button) {
      this.list.scrollTop = 0;
      return;
    }

    // Layout may not be resolved yet on first paint (stylesheets load in
    // parallel with this script), and every offset would read as zero. Retry on
    // the next frame instead of scrolling to a meaningless position.
    //
    // On mobile the inline copy of a picker sits `display: none` while the
    // modal owns the visible one, so it measures zero *permanently* rather
    // than just on first paint — cap the retries or this becomes a
    // requestAnimationFrame loop that runs for the rest of the page's life.
    if (this.list.clientHeight === 0 || button.offsetHeight === 0) {
      if (attempt >= 10 || this.root.offsetParent === null) return;
      window.requestAnimationFrame(() => this.scrollToValue(false, attempt + 1));
      return;
    }

    const target = Math.max(0, this.offsetForButton(button));
    if (Math.abs(this.list.scrollTop - target) < 1) return;

    // Suppress the scroll handler while we move the list ourselves, otherwise
    // settling would re-emit the value we just set.
    this.programmaticScroll = true;
    this.list.scrollTo({ top: target, behavior: smooth ? 'smooth' : 'auto' });

    window.clearTimeout(this.programmaticTimer);
    this.programmaticTimer = window.setTimeout(() => {
      this.programmaticScroll = false;
    }, smooth ? 400 : 100);
  }

  /**
   * Detaching a scrolled list resets its scrollTop, which the scroll-settle
   * handler would otherwise read as "the user chose the first option" and
   * cascade into clearing every later step. Freeze the handler while we move.
   */
  suppressScrollSettle(duration) {
    this.programmaticScroll = true;
    window.clearTimeout(this.scrollTimer);
    window.clearTimeout(this.programmaticTimer);
    this.programmaticTimer = window.setTimeout(() => {
      this.programmaticScroll = false;
    }, duration);
  }

  /**
   * Move this picker's DOM node — the mobile modal opens it inside a slot
   * where --vc-visible-rows resolves to 5 instead of the inline 3 — and
   * re-centre for the new row count. The node keeps its listeners/state, so
   * nothing needs to be duplicated or re-synced.
   */
  relocate(parent, before) {
    this.suppressScrollSettle(300);

    if (before && before.parentNode === parent) parent.insertBefore(this.root, before);
    else parent.appendChild(this.root);

    // Force a synchronous layout so the pad/list heights resolve against the
    // destination's --vc-visible-rows before offsetForButton() measures them.
    void this.list.offsetHeight;
    this.scrollToValue(false);
  }

  handleScroll() {
    if (this.disabled || this.programmaticScroll || this.options.length === 0) return;

    window.clearTimeout(this.scrollTimer);
    this.scrollTimer = window.setTimeout(() => {
      const index = this.indexAtCentre();
      const option = index < 0 ? null : this.options[index];
      if (!option || option.value === this.lastEmitted) return;

      this.value = option.value;
      this.lastEmitted = option.value;
      this.syncSelectedAttributes();
      this.onChange(this.field, option.value);
    }, 90);
  }

  handleKeydown(event) {
    if (this.disabled || this.options.length === 0) return;

    const current = this.options.findIndex((option) => option.value === this.value);
    const from = current < 0 ? 0 : current;
    let next = null;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = from + 1;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        next = from - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.options.length - 1;
        break;
      case 'PageDown':
        next = from + 3;
        break;
      case 'PageUp':
        next = from - 3;
        break;
      default:
        return;
    }

    event.preventDefault();
    const clamped = Math.max(0, Math.min(this.options.length - 1, next));
    const option = this.options[clamped];
    if (!option) return;

    this.setValue(option.value);
    const button = this.list.querySelector(`[data-index="${clamped}"]`);
    if (button) button.focus({ preventScroll: true });
  }
}

class VehicleConfigurator {
  constructor(root) {
    this.root = root;
    this.data = this.readData();
    if (!this.data) return;

    this.selection = { brand: null, model: null, year: null, mileage: null };
    this.pickers = {};
    this.triggers = {};

    root.querySelectorAll('[data-step-trigger]').forEach((button) => {
      this.triggers[button.dataset.field] = button;
    });

    // Where each picker lives when the modal is closed, captured once before
    // any relocation — repeated open/close cycles can never drift out of
    // position since this never gets recomputed later.
    this.pickerHomes = {};

    root.querySelectorAll('[data-wheel-picker]').forEach((element) => {
      const field = element.dataset.field;
      this.pickerHomes[field] = { parent: element.parentNode, next: element.nextSibling };
      this.pickers[field] = new WheelPicker(element, {
        trigger: this.triggers[field] || null,
        onChange: (f, value) => this.handlePickerChange(f, value),
        onPick: () => this.closePickerModal({ focusTrigger: true }),
      });
    });

    this.pickersGrid = root.querySelector('[data-pickers]');
    this.statusEl = root.querySelector('[data-status]');
    this.bundleButtons = Array.from(root.querySelectorAll('[data-bundle]'));
    this.plateInput = root.querySelector('[data-plate-input]');
    this.plateClear = root.querySelector('[data-plate-clear]');
    this.plateRow = root.querySelector('[data-plate-row]');
    this.plateTrigger = root.querySelector('[data-plate-trigger]');
    this.plateHome = this.plateRow
      ? { parent: this.plateRow.parentNode, next: this.plateRow.nextSibling }
      : null;

    // The picker modal is a sibling of .vehicle-configurator__card (not a
    // descendant of `root`) so it sits outside the card's overflow-x:hidden
    // ancestor — look it up from the whole section instead.
    this.section = root.closest('.vehicle-configurator') || root;
    this.modal = this.section.querySelector('[data-picker-modal]');
    this.modalSlot = this.modal ? this.modal.querySelector('[data-picker-slot]') : null;
    this.modalDialog = this.modal ? this.modal.querySelector('[data-picker-dialog]') : null;
    this.modalTitle = this.modal ? this.modal.querySelector('[data-picker-modal-title]') : null;
    this.modalOpenField = null;
    this.mobileQuery = window.matchMedia('(max-width: 749px)');

    this.bundlesContainer = this.bundleButtons.length ? root.querySelector('.vehicle-configurator__bundles') : null;

    this.bindPickerModal();
    this.populateStaticPickers();
    this.bindPlate();
    this.bindBundles();
    this.update();
  }

  bindBundles() {
    if (!this.bundlesContainer) return;
    const variantId = this.bundlesContainer.dataset.variantId;
    const cartAddUrl = this.bundlesContainer.dataset.cartAddUrl;
    if (!variantId) return;

    this.bundleButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        this.purchaseBundle(button, variantId, cartAddUrl);
      });
    });
  }

  purchaseBundle(button, variantId, cartAddUrl) {
    const quantity = Number.parseInt(button.dataset.reports, 10) || 1;
    const vehicle = this.buildSummary();

    this.bundleButtons.forEach((b) => {
      b.disabled = true;
    });
    button.classList.add('vehicle-configurator__bundle--loading');

    const properties = {};
    if (vehicle) properties.Vehicle = vehicle;

    fetch(cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity, properties }],
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart add failed');
        window.location.href = '/checkout';
      })
      .catch((error) => {
        console.error('Vehicle configurator: failed to add report to cart', error);
        button.classList.remove('vehicle-configurator__bundle--loading');
        this.update();
      });
  }

  readData() {
    const script = this.root.querySelector('[data-configurator-data]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Vehicle configurator: invalid data island', error);
      return null;
    }
  }

  populateStaticPickers() {
    if (this.pickers.brand) {
      this.pickers.brand.setOptions(
        this.data.brands.map((brand) => ({
          value: brand.id,
          label: brand.name,
          icon: brand.icon || null,
        }))
      );
    }

    if (this.pickers.year) {
      this.pickers.year.setOptions(
        this.data.years.map((year) => ({ value: String(year), label: String(year) }))
      );
    }

    if (this.pickers.mileage) {
      this.pickers.mileage.setOptions(
        this.data.mileages.map((km) => ({
          value: String(km),
          label: this.formatMileage(km),
        }))
      );
    }

    if (this.pickers.model) {
      this.pickers.model.setOptions([]);
    }
  }

  formatMileage(km) {
    const formatted = Number(km).toLocaleString('en-US');
    const suffix = this.data.labels.mileageSuffix;
    return suffix ? `${formatted} ${suffix}` : formatted;
  }

  handlePickerChange(field, value) {
    this.selection[field] = value;

    // Switching manufacturer invalidates whatever model was chosen before.
    if (field === 'brand') {
      this.selection.model = null;
      const brand = this.data.brands.find((item) => item.id === value);
      const models = brand ? brand.models : [];
      if (this.pickers.model) {
        this.pickers.model.clear();
        this.pickers.model.setOptions(
          models.map((model) => ({ value: model, label: model }))
        );
      }
    }

    // Each step's choice only makes sense in light of the ones before it, so
    // changing an earlier step clears everything gated behind it.
    if (field === 'brand' || field === 'model') {
      this.selection.year = null;
      if (this.pickers.year) this.pickers.year.clear();
    }
    if (field === 'brand' || field === 'model' || field === 'year') {
      this.selection.mileage = null;
      if (this.pickers.mileage) this.pickers.mileage.clear();
    }

    this.update();
  }

  bindPlate() {
    if (!this.plateInput) return;

    this.plateInput.addEventListener('input', () => this.update());

    if (this.plateClear) {
      this.plateClear.addEventListener('click', () => {
        this.plateInput.value = '';
        this.plateInput.focus();
        this.update();
      });
    }

    if (this.plateTrigger) {
      this.plateTrigger.addEventListener('click', () => this.openPlateModal());
    }
  }

  bindPickerModal() {
    if (!this.modal) return;

    // A Theme Editor re-render can leave a stale body lock behind.
    this.closePickerModal({ silent: true });

    Object.keys(this.triggers).forEach((field) => {
      this.triggers[field].addEventListener('click', () => this.openPickerModal(field));
    });

    // Backdrop: the modal root itself is the full-screen click-catcher, so a
    // click on any descendant (the dialog, the picker, an option) fails this
    // check — same contract as Dawn's ModalDialog in global.js.
    this.modal.addEventListener('click', (event) => {
      if (event.target === this.modal) this.closePickerModal({ focusTrigger: true });
    });

    this.modal.addEventListener('keyup', (event) => {
      if (event.code && event.code.toUpperCase() === 'ESCAPE') {
        this.closePickerModal({ focusTrigger: true });
      }
    });

    const close = this.modal.querySelector('[data-picker-close]');
    if (close) close.addEventListener('click', () => this.closePickerModal({ focusTrigger: true }));

    // Resizing/rotating past the breakpoint with the modal open would
    // otherwise strand the picker inside a display:none overlay.
    const onBreakpointChange = (event) => {
      if (!event.matches) this.closePickerModal();
    };
    if (this.mobileQuery.addEventListener) this.mobileQuery.addEventListener('change', onBreakpointChange);
    else this.mobileQuery.addListener(onBreakpointChange);
  }

  openPickerModal(field) {
    const picker = this.pickers[field];
    const trigger = this.triggers[field];
    if (!this.modal || !picker || !trigger || trigger.disabled) return;
    if (this.modalOpenField) this.closePickerModal({ silent: true });

    this.modalOpenField = field;
    this.modalOpener = trigger;
    if (this.modalTitle) this.modalTitle.textContent = trigger.dataset.label || '';

    this.lockBodyScroll();
    this.modal.setAttribute('open', '');

    // Styles for the 5-row modal context are live now; move + re-centre.
    picker.relocate(this.modalSlot, null);

    this.deferFocus(() => {
      if (typeof trapFocus === 'function') trapFocus(this.modal, this.modalDialog);
      else if (this.modalDialog) this.modalDialog.focus();
    });
  }

  /**
   * Focusing right after opening the modal has two race conditions to dodge:
   * 1. The browser's own default click-activation for the trigger <button>
   *    re-focuses it right after this handler runs, clobbering a focus()
   *    call made synchronously here.
   * 2. The modal's `visibility` only actually flips from hidden to visible
   *    once the browser has run a style/paint pass for the `[open]` change —
   *    querying/focusing synchronously (or even on the very next task) can
   *    still observe `visibility: hidden`, which silently no-ops focus().
   * A double requestAnimationFrame reliably lands after that first paint.
   */
  deferFocus(fn) {
    window.requestAnimationFrame(() => window.requestAnimationFrame(fn));
  }

  /**
   * Dawn's plain `.overflow-hidden` (overflow: hidden on body) is not
   * enough on iOS Safari — it still lets the page rubber-band/scroll behind
   * a fixed-position overlay via touch. Pinning the body with
   * `position: fixed` (restoring the exact scroll offset on close) is the
   * standard fix that actually stops the background from moving at all
   * while a picker/plate modal is open.
   */
  lockBodyScroll() {
    if (document.body.dataset.vcModalOpen === 'true') return;
    this.savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.dataset.vcModalOpen = 'true';
    document.body.classList.add('overflow-hidden');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  unlockBodyScroll() {
    if (document.body.dataset.vcModalOpen !== 'true') return;
    delete document.body.dataset.vcModalOpen;
    document.body.classList.remove('overflow-hidden');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, this.savedScrollY || 0);
  }

  /**
   * The plate number field isn't a WheelPicker — it's a live text input —
   * so it gets its own open path rather than going through openPickerModal.
   * It still shares the same modal shell/backdrop/Escape/focus-trap wiring,
   * tracked the same way via `modalOpenField` (set to the literal 'plate').
   */
  openPlateModal() {
    if (!this.modal || !this.plateRow || !this.plateTrigger || this.plateTrigger.disabled) return;
    if (this.modalOpenField) this.closePickerModal({ silent: true });

    this.modalOpenField = 'plate';
    this.modalOpener = this.plateTrigger;
    if (this.modalTitle) this.modalTitle.textContent = this.plateTrigger.dataset.label || '';

    this.lockBodyScroll();
    this.modal.setAttribute('open', '');

    this.modalSlot.appendChild(this.plateRow);

    // Unlike the wheel pickers (which focus the dialog itself to avoid an
    // awkward first-option focus), a text field modal should focus the
    // input directly so the keyboard opens immediately.
    this.deferFocus(() => {
      if (typeof trapFocus === 'function') trapFocus(this.modal, this.plateInput);
      else if (this.plateInput) this.plateInput.focus({ preventScroll: true });
    });
  }

  closePickerModal({ focusTrigger = false, silent = false } = {}) {
    if (!this.modal) return;
    const field = this.modalOpenField;
    this.modalOpenField = null;

    this.modal.removeAttribute('open');
    this.unlockBodyScroll();

    if (field === 'plate') {
      if (this.plateRow && this.plateHome) {
        if (this.plateHome.next && this.plateHome.next.parentNode === this.plateHome.parent) {
          this.plateHome.parent.insertBefore(this.plateRow, this.plateHome.next);
        } else {
          this.plateHome.parent.appendChild(this.plateRow);
        }
      }
    } else if (field && this.pickers[field]) {
      const home = this.pickerHomes[field];
      this.pickers[field].relocate(home.parent, home.next);
    }

    if (silent) return;
    const opener = focusTrigger ? this.modalOpener : null;
    if (typeof removeTrapFocus === 'function') removeTrapFocus(opener);
    else if (opener) opener.focus();
    this.modalOpener = null;
  }

  /** Text a trigger button should display for a field, or null if unset. */
  displayValueFor(field) {
    const value = this.selection[field];
    if (!value) return null;
    if (field === 'brand') {
      const brand = this.data.brands.find((item) => item.id === value);
      return brand ? brand.name : null;
    }
    if (field === 'mileage') return this.formatMileage(value);
    return String(value);
  }

  syncTriggers() {
    Object.keys(this.triggers).forEach((field) => {
      const trigger = this.triggers[field];
      const valueEl = trigger.querySelector('[data-trigger-value]');
      const label = trigger.dataset.label || '';
      const placeholder = trigger.dataset.placeholder || '';
      const display = this.displayValueFor(field);

      if (valueEl) {
        valueEl.textContent = display || placeholder;
        valueEl.dataset.empty = String(!display);
      }
      trigger.setAttribute('aria-label', `${label}: ${display || placeholder}`);
    });

    if (this.plateTrigger) {
      const valueEl = this.plateTrigger.querySelector('[data-plate-trigger-value]');
      const placeholder = this.plateTrigger.dataset.placeholder || '';
      const display = this.plateValue;

      if (valueEl) {
        valueEl.textContent = display || placeholder;
        valueEl.dataset.empty = String(!display);
      }
      const label = this.plateTrigger.dataset.label || '';
      this.plateTrigger.setAttribute('aria-label', `${label}: ${display || placeholder}`);
    }
  }

  get plateValue() {
    return this.plateInput ? this.plateInput.value.trim() : '';
  }

  get plateActive() {
    return this.plateValue.length > 0;
  }

  get isReady() {
    // A plate number stands in for manufacturer, model and year, so only the
    // mileage is still required in that mode.
    if (this.plateActive) return Boolean(this.selection.mileage);
    return Boolean(
      this.selection.brand && this.selection.model && this.selection.year && this.selection.mileage
    );
  }

  update() {
    const plateActive = this.plateActive;
    const locked = this.data.plateLockedFields || [];

    if (this.pickersGrid) {
      this.pickersGrid.dataset.plateActive = String(plateActive);
    }

    locked.forEach((field) => {
      if (this.pickers[field]) this.pickers[field].setDisabled(plateActive);
    });

    // Steps unlock one at a time: model needs a brand, year needs a model,
    // mileage needs a year. Plate mode bypasses brand/model/year entirely
    // (locked above), so this sequential gating only applies when it's off.
    if (!plateActive) {
      if (this.pickers.model) this.pickers.model.setDisabled(!this.selection.brand);
      if (this.pickers.year) this.pickers.year.setDisabled(!this.selection.model);
    }
    if (this.pickers.mileage) {
      this.pickers.mileage.setDisabled(plateActive ? false : !this.selection.year);
    }

    if (this.plateClear) {
      this.plateClear.hidden = !plateActive;
    }

    const ready = this.isReady;

    if (this.statusEl) {
      this.statusEl.dataset.ready = String(ready);
      this.statusEl.textContent = ready
        ? this.buildSummary()
        : this.data.labels.statusIncomplete;
    }

    this.bundleButtons.forEach((button) => {
      button.disabled = !ready;
    });

    this.syncTriggers();
  }

  buildSummary() {
    const parts = [];

    if (this.plateActive) {
      parts.push(this.plateValue);
    } else {
      const brand = this.data.brands.find((item) => item.id === this.selection.brand);
      if (brand) parts.push(brand.name);
      if (this.selection.model) parts.push(this.selection.model);
      if (this.selection.year) parts.push(this.selection.year);
    }

    if (this.selection.mileage) parts.push(this.formatMileage(this.selection.mileage));

    return parts.join(' · ');
  }
}

function initVehicleConfigurators() {
  document.querySelectorAll('[data-vehicle-configurator]').forEach((root) => {
    if (root.dataset.initialised === 'true') return;
    root.dataset.initialised = 'true';
    new VehicleConfigurator(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVehicleConfigurators);
} else {
  initVehicleConfigurators();
}

// Re-initialise when the Theme Editor re-renders this section.
document.addEventListener('shopify:section:load', (event) => {
  const root = event.target.querySelector('[data-vehicle-configurator]');
  if (root && root.dataset.initialised !== 'true') {
    root.dataset.initialised = 'true';
    new VehicleConfigurator(root);
  }
});
